import {defineStore} from "pinia";
import axios from "axios";
import {IAvailableModels, IAgentStore, IDiscoverMatch} from "./Interfaces/IAgentStore";
import {computed, Ref, ref, watch} from "vue";
import useWrapper from "@/composables/useWrapper";
import type {IAgent} from "./Interfaces/IAgent";
import {TAgentStoreDefinition} from "./Types/TAgentStoreDefinition";
import {useApiCredentials} from "@/composables/useApiCredentials";
import {useSSE} from "@/composables/useSSE";

// ─── Agent Definition Interfaces ────────────────────────────────────────────────────

export interface SubAgent {
    id: number
    app_id: number
    name: string
    description: string
    instructions: string
    model: string
    tools: string[]
    output_schema: Record<string, unknown> | null
    max_retries: number
    tags: string[]
    is_global: boolean
    status: string
    is_system: boolean
    sub_agent_ids: number[]
    workflow_ids: number[]
    config: Record<string, unknown> | null
    team: string | null
    sub_team: string | null
    team_id: number | null
    sub_team_id: number | null
    is_team_coordinator: boolean
    visibility: string | null
    created_at: string
    updated_at: string
}

export interface SubAgentTeam {
    id: number
    app_id: number
    name: string
    description: string
    icon: string
    parent_team_id: number | null
    created_at: string
    updated_at: string
}

export interface SubAgentPhonebookEntry {
    name: string
    description: string
    tags: string[]
    model: string
    tools: string[]
    has_structured_output: boolean
    app_id: number
    is_global: boolean
    compatible: boolean
    incompatible_tools: string[]
    team: string | null
    sub_team: string | null
    visibility: string | null
}

export interface AgentSession {
    id: number
    agent_id: number
    title: string
    created_at: string
    updated_at: string
}

export interface SubAgentDiscoveryFilters {
    team?: string
    sub_team?: string
    visibility?: string
}

export interface CreateSubAgentRequest {
    name: string
    description?: string
    instructions?: string
    model?: string
    tools?: string[]
    output_schema?: Record<string, unknown> | null
    max_retries?: number
    tags?: string[]
    is_global?: boolean
    is_system?: boolean
    sub_agent_ids?: number[]
    workflow_ids?: number[]
    config?: Record<string, unknown> | null
    create_linked_bucket?: boolean
    team?: string | null
    sub_team?: string | null
    team_id?: number | null
    sub_team_id?: number | null
    is_team_coordinator?: boolean
    visibility?: string | null
}

export interface UpdateSubAgentRequest {
    name?: string
    description?: string
    instructions?: string
    model?: string
    tools?: string[]
    output_schema?: Record<string, unknown> | null
    max_retries?: number
    tags?: string[]
    is_global?: boolean
    is_system?: boolean
    sub_agent_ids?: number[]
    workflow_ids?: number[]
    config?: Record<string, unknown> | null
    team?: string | null
    sub_team?: string | null
    team_id?: number | null
    sub_team_id?: number | null
    is_team_coordinator?: boolean
    visibility?: string | null
}

export interface CreateSubAgentTeamRequest {
    name: string
    description?: string
    icon?: string
    parent_team_id?: number | null
}

export interface UpdateSubAgentTeamRequest {
    name?: string
    description?: string
    icon?: string
}

// ─── Provider types ──────────────────────────────────────────────────────────

// Provider types from kmlib const.py
type ProviderType = 'azure' | 'google' | 'openai' | 'perplexity' | 'ollama'
type ModelProviderType = 'open_ai' | 'google' | 'perplexity' | 'anthropic'

// Anthropic Thinking configuration type (from kmlib/llm/base/anthropic_specific.py)
type AnthropicThinkingEffort = 'low' | 'medium' | 'high' | 'max'

interface AnthropicThinking {
    budget_tokens?: number | null
    effort: AnthropicThinkingEffort
    interleaved_thoughts: boolean
    adaptive_thoughts: boolean
}

interface AnthropicCaching {
    instructions: boolean
    tool_definitions: boolean
    messages: boolean
}

// Valid combinations from kmlib const.py LLM_PROVIDER_COMBINATIONS
const LLM_PROVIDER_COMBINATIONS: Record<ProviderType, ModelProviderType[]> = {
    'azure': ['open_ai', 'anthropic'],
    'google': ['google', 'open_ai'],
    'openai': ['open_ai'],
    'perplexity': ['perplexity'],
    'ollama': ['open_ai'],
}

// Tool types from PublicLLmConfig
type ToolType = 'get_current_date' | 'search_web' | 'http_request' | 'wait' | 'knowledge_base_search' | 'code_execution' | 'image_generation' | 'list_artifacts'

// Output config type
type OutputType = 'string' | 'structured'

// Skill types from KMLib Skill enum
type SkillType = 'summarization' | 'todo' | 'data'

// Message interface for history
interface ChatMessage {
    key: string
    content: string
}

// Usage limits interface
interface UsageLimits {
    request_limit?: number | null
    request_tokens_limit?: number | null
    response_tokens_limit?: number | null
    total_tokens_limit?: number | null
}

interface ArtifactReferencePayload {
    key?: string
    artifact_ref: string
}

export const useAgentStore: TAgentStoreDefinition = defineStore(
    "agentStore",
    (): IAgentStore => {
        const AGENT_DEBUG_PREFIX = '[AgentHistoryDebug][Store]';
        const agentDebugEnabled = (): boolean => true;
        const debugLog = (message: string, payload?: unknown): void => {
            if (!agentDebugEnabled()) return;
            if (payload === undefined) {
                console.debug(`${AGENT_DEBUG_PREFIX} ${message}`);
                return;
            }
            console.debug(`${AGENT_DEBUG_PREFIX} ${message}`, payload);
        };
        debugLog('logger_initialized', {
            mode: import.meta.env.MODE,
            dev: import.meta.env.DEV,
        });

        const initialized: Ref<boolean> = ref(false);
        const items: Ref<IAgent[]> = ref([]);
        const models: Ref<IAvailableModels[]> = ref([]);

        // Provider configuration
        const cloud_provider: Ref<ProviderType> = ref('azure');
        const aiProvider: Ref<ModelProviderType> = ref('open_ai');

        // Model and generation parameters
        const currentModel: Ref<string> = ref("");
        const temperature: Ref<number> = ref(1);
        const maxTokens: Ref<number | null> = ref(null);
        const topP: Ref<number> = ref(1);
        const timeout: Ref<number> = ref(300);
        const seed: Ref<number | null> = ref(null);
        const samplingMethod: Ref<'temperature' | 'top_p'> = ref('temperature');

        // Penalties
        const presencePenalty: Ref<number> = ref(0);
        const frequencyPenalty: Ref<number> = ref(0);

        // Reasoning (for o1/o3/o4 models)
        const reasoningEffort: Ref<'low' | 'medium' | 'high' | null> = ref(null);
        const reasoningSummary: Ref<'concise' | 'detailed' | null> = ref(null);

        // Anthropic Thinking (for Claude models with extended thinking)
        const anthropicThinkingEnabled: Ref<boolean> = ref(false);
        const anthropicThinkingEffort: Ref<AnthropicThinkingEffort> = ref('medium');
        const anthropicThinkingBudgetTokens: Ref<number | null> = ref(null);
        const anthropicInterleavedThoughts: Ref<boolean> = ref(false);
        const anthropicAdaptiveThoughts: Ref<boolean> = ref(false);

        // Anthropic Caching
        const anthropicCachingEnabled: Ref<boolean> = ref(false);
        const anthropicCacheInstructions: Ref<boolean> = ref(true);
        const anthropicCacheToolDefinitions: Ref<boolean> = ref(true);
        const anthropicCacheMessages: Ref<boolean> = ref(false);

        // Perplexity specific
        const searchMode: Ref<'academic' | 'sec' | null> = ref(null);
        const searchRecencyFilter: Ref<'day' | 'week' | 'month' | 'year' | null> = ref(null);
        const searchLanguageFilter: Ref<string[]> = ref([]);
        const searchDomainFilter: Ref<string[]> = ref([]);
        const searchAfterDateFilter: Ref<string | null> = ref(null);
        const searchBeforeDateFilter: Ref<string | null> = ref(null);
        const lastUpdatedAfterFilter: Ref<string | null> = ref(null);
        const lastUpdatedBeforeFilter: Ref<string | null> = ref(null);
        const searchContextSize: Ref<'low' | 'medium' | 'high' | null> = ref(null);
        const userLocationCountry: Ref<string | null> = ref(null);
        const userLocationLatitude: Ref<number | null> = ref(null);
        const userLocationLongitude: Ref<number | null> = ref(null);
        const userLocationCity: Ref<string | null> = ref(null);
        const userLocationRegion: Ref<string | null> = ref(null);
        const includeCitations: Ref<boolean> = ref(true);

        // Ollama specific
        const ollamaThink: Ref<boolean | 'low' | 'medium' | 'high' | null> = ref(null);

        // Tools and MCP
        const availableTools: Ref<ToolType[]> = ref([]);
        const mcpServers: Ref<('playwright' | 'azdevops')[]> = ref([]);
        const knowledgeBaseIndexes: Ref<string[]> = ref([]);

        // Retry configuration
        const retries: Ref<number> = ref(10);
        const outputRetries: Ref<number> = ref(10);

        // Agent behaviour
        const endStrategy: Ref<'early' | 'exhaustive'> = ref('exhaustive');
        const toolTimeout: Ref<number | null> = ref(null);
        const maxConcurrency: Ref<number | null> = ref(null);

        // Tool execution mode
        const parallelToolCalls: Ref<boolean> = ref(false);

        // History - stores pydantic-ai message format from AgentResponseModel.history
        const historyLength: Ref<number | null> = ref(null);
        // History in pydantic-ai format (ModelRequest/ModelResponse objects)
        const conversationHistory: Ref<any[]> = ref([]);
            const sessionTranscript: Ref<Array<{ role: 'user' | 'assistant'; content: string }>> = ref([]);
        const agentSessions: Ref<AgentSession[]> = ref([]);
        const activeSessionId: Ref<number | null> = ref(null);
        const sessionLoading: Ref<boolean> = ref(false);

        // Output config for structured outputs
        const outputType: Ref<OutputType> = ref('string');
        const outputSchema: Ref<Record<string, any> | null> = ref(null);
        const outputModelName: Ref<string> = ref('ResponseModel');

        // Memory configuration
        const memoryEnabled: Ref<boolean> = ref(false);
        const memoryAgentName: Ref<string> = ref('agent');

        // Message
        const messageKey: Ref<string> = ref("");
        const message: Ref<string> = ref("");
        const artifactRefs: Ref<ArtifactReferencePayload[]> = ref([]);
        const prompt: Ref<string> = ref("You are a helpful assistant.");

        // Streaming
        const sse = useSSE();

        // ── Skills ───────────────────────────────────────────────────────
        const skills: Ref<SkillType[]> = ref([]);
        const subAgentIds: Ref<number[]> = ref([]);
        const workflowIds: Ref<number[]> = ref([]);
        const guardrailIds: Ref<number[]> = ref([]);
        const outputValidation: Ref<string[]> = ref([]);
        const usageLimits: Ref<UsageLimits> = ref({});

        // ── Summarization Config ─────────────────────────────────────────
        const summarizationMaxMessages: Ref<number> = ref(50);
        const summarizationMaxTokens: Ref<number | null> = ref(null);
        const summarizationKeepRecent: Ref<number> = ref(10);

        // ── Agent Definition State ──────────────────────────────────────────────
        const subAgents: Ref<SubAgent[]> = ref([]);
        const subAgentTeams: Ref<SubAgentTeam[]> = ref([]);
        const phonebook: Ref<SubAgentPhonebookEntry[]> = ref([]);
        const currentSubAgent: Ref<SubAgent | null> = ref(null);
        const subAgentLoading: Ref<boolean> = ref(false);

        // ── @Mention Delegation ─────────────────────────────────────────
        /** When set, signals the backend to prepend a delegation directive for the named sub-agent. */
        const targetHint: Ref<string | null> = ref(null);

        // ── Loaded Agent Tracking ────────────────────────────────────────
        /** ID of the saved agent currently loaded into the chat config, or null for default/scratch. */
        const loadedAgentId: Ref<number | null> = ref(null);
        /** Name of the loaded agent (for display in UI). */
        const loadedAgentName: Ref<string> = ref('');
        /** Whether the loaded agent is system-relevant (bypasses approval). */
        const loadedAgentIsSystem: Ref<boolean> = ref(false);

        // ── Default Config Snapshot ──────────────────────────────────────
        const DEFAULT_PROMPT = 'You are a helpful assistant.';
        const DEFAULT_TEMPERATURE = 1;
        const DEFAULT_TOP_P = 1;
        const DEFAULT_TIMEOUT = 300;
        const DEFAULT_RETRIES = 10;
        const DEFAULT_OUTPUT_RETRIES = 10;

        /** True when the current chat config differs from baseline defaults. */
        const isNonDefaultConfig = computed(() => {
            if (loadedAgentId.value) return true;
            if (prompt.value !== DEFAULT_PROMPT) return true;
            if (currentModel.value && models.value.length > 0 && currentModel.value !== models.value[0]?.name) return true;
            if (availableTools.value.length > 0) return true;
            if (mcpServers.value.length > 0) return true;
            if (skills.value.length > 0) return true;
            if (subAgentIds.value.length > 0) return true;
            if (outputType.value !== 'string') return true;
            if (temperature.value !== DEFAULT_TEMPERATURE) return true;
            if (retries.value !== DEFAULT_RETRIES) return true;
            if (knowledgeBaseIndexes.value.length > 0) return true;
            if (includeCitations.value !== true) return true;
            return false;
        });

        /**
         * Build a full config snapshot from the current UI state.
         * This is saved alongside agent metadata so the full config
         * can be restored when loading the agent.
         */
        const buildFullConfig = (): Record<string, unknown> => ({
            model: currentModel.value,
            cloud_provider: cloud_provider.value,
            model_provider: aiProvider.value,
            prompt: prompt.value,
            temperature: temperature.value,
            top_p: topP.value,
            sampling_method: samplingMethod.value,
            timeout: timeout.value,
            max_tokens: maxTokens.value,
            seed: seed.value,
            presence_penalty: presencePenalty.value,
            frequency_penalty: frequencyPenalty.value,
            parallel_tool_calls: parallelToolCalls.value,
            // Reasoning
            reasoning_effort: reasoningEffort.value,
            reasoning_summary: reasoningSummary.value,
            // Anthropic
            anthropic_thinking_enabled: anthropicThinkingEnabled.value,
            anthropic_thinking_effort: anthropicThinkingEffort.value,
            anthropic_thinking_budget_tokens: anthropicThinkingBudgetTokens.value,
            anthropic_interleaved_thoughts: anthropicInterleavedThoughts.value,
            anthropic_adaptive_thoughts: anthropicAdaptiveThoughts.value,
            anthropic_caching_enabled: anthropicCachingEnabled.value,
            anthropic_cache_instructions: anthropicCacheInstructions.value,
            anthropic_cache_tool_definitions: anthropicCacheToolDefinitions.value,
            anthropic_cache_messages: anthropicCacheMessages.value,
            // Perplexity
            search_mode: searchMode.value,
            search_recency_filter: searchRecencyFilter.value,
            search_language_filter: searchLanguageFilter.value,
            search_domain_filter: searchDomainFilter.value.length > 0 ? searchDomainFilter.value : null,
            search_after_date_filter: searchAfterDateFilter.value,
            search_before_date_filter: searchBeforeDateFilter.value,
            last_updated_after_filter: lastUpdatedAfterFilter.value,
            last_updated_before_filter: lastUpdatedBeforeFilter.value,
            search_context_size: searchContextSize.value,
            user_location_country: userLocationCountry.value,
            user_location_latitude: userLocationLatitude.value,
            user_location_longitude: userLocationLongitude.value,
            user_location_city: userLocationCity.value,
            user_location_region: userLocationRegion.value,
            include_citations: includeCitations.value,
            // Ollama
            ollama_think: ollamaThink.value,
            // Tools & MCP
            available_tools: availableTools.value,
            mcp_servers: mcpServers.value,
            knowledge_base_indexes: knowledgeBaseIndexes.value,
            // Retries
            retries: retries.value,
            output_retries: outputRetries.value,
            // Agent behaviour
            end_strategy: endStrategy.value,
            tool_timeout: toolTimeout.value,
            max_concurrency: maxConcurrency.value,
            // History
            history_length: historyLength.value,
            // Output
            output_type: outputType.value,
            output_schema: outputSchema.value,
            output_model_name: outputModelName.value,
            // Skills
            skills: skills.value,
            workflow_ids: workflowIds.value,
            // Summarization
            summarization_max_messages: summarizationMaxMessages.value,
            summarization_max_tokens: summarizationMaxTokens.value,
            summarization_keep_recent: summarizationKeepRecent.value,
            guardrail_ids: guardrailIds.value,
            output_validation: outputValidation.value,
            usage_limits: usageLimits.value,
            // Memory
            memory_enabled: memoryEnabled.value,
            memory_agent_name: memoryAgentName.value,
        });

        /**
         * Load a saved agent's full config into the chat UI state.
         * Restores all parameters from the agent's config snapshot.
         */
        const loadAgentConfig = (agent: SubAgent) => {
            const c = agent.config as Record<string, any> | null;

            // Always restore basic fields from the agent model
            prompt.value = agent.instructions || DEFAULT_PROMPT;
            if (agent.model && models.value.some(m => m.name === agent.model)) {
                currentModel.value = agent.model;
            }
            availableTools.value = (agent.tools || []) as ToolType[];
            retries.value = agent.max_retries ?? DEFAULT_RETRIES;
            if (agent.output_schema) {
                outputType.value = 'structured';
                outputSchema.value = { ...agent.output_schema };
            } else {
                outputType.value = 'string';
                outputSchema.value = null;
            }
            subAgentIds.value = agent.sub_agent_ids ? [...agent.sub_agent_ids] : [];
            workflowIds.value = agent.workflow_ids ? [...agent.workflow_ids] : [];
            includeCitations.value = true;

            // If full config snapshot exists, restore all detailed params
            if (c) {
                if (c.model && models.value.some(m => m.name === c.model)) currentModel.value = c.model;
                if (c.cloud_provider !== undefined) cloud_provider.value = c.cloud_provider;
                if (c.model_provider !== undefined) aiProvider.value = c.model_provider;
                if (c.prompt !== undefined) prompt.value = c.prompt;
                if (c.temperature !== undefined) temperature.value = c.temperature;
                if (c.top_p !== undefined) topP.value = c.top_p;
                if (c.sampling_method) samplingMethod.value = c.sampling_method;
                if (c.timeout !== undefined) timeout.value = c.timeout;
                if (c.max_tokens !== undefined) maxTokens.value = c.max_tokens;
                if (c.seed !== undefined) seed.value = c.seed;
                if (c.presence_penalty !== undefined) presencePenalty.value = c.presence_penalty;
                if (c.frequency_penalty !== undefined) frequencyPenalty.value = c.frequency_penalty;
                if (c.parallel_tool_calls !== undefined) parallelToolCalls.value = c.parallel_tool_calls;
                // Reasoning
                if (c.reasoning_effort !== undefined) reasoningEffort.value = c.reasoning_effort;
                if (c.reasoning_summary !== undefined) reasoningSummary.value = c.reasoning_summary;
                // Anthropic
                if (c.anthropic_thinking_enabled !== undefined) anthropicThinkingEnabled.value = c.anthropic_thinking_enabled;
                if (c.anthropic_thinking_effort) anthropicThinkingEffort.value = c.anthropic_thinking_effort;
                if (c.anthropic_thinking_budget_tokens !== undefined) anthropicThinkingBudgetTokens.value = c.anthropic_thinking_budget_tokens;
                if (c.anthropic_interleaved_thoughts !== undefined) anthropicInterleavedThoughts.value = c.anthropic_interleaved_thoughts;
                if (c.anthropic_adaptive_thoughts !== undefined) anthropicAdaptiveThoughts.value = c.anthropic_adaptive_thoughts;
                if (c.anthropic_caching_enabled !== undefined) anthropicCachingEnabled.value = c.anthropic_caching_enabled;
                if (c.anthropic_cache_instructions !== undefined) anthropicCacheInstructions.value = c.anthropic_cache_instructions;
                if (c.anthropic_cache_tool_definitions !== undefined) anthropicCacheToolDefinitions.value = c.anthropic_cache_tool_definitions;
                if (c.anthropic_cache_messages !== undefined) anthropicCacheMessages.value = c.anthropic_cache_messages;
                // Perplexity
                if (c.search_mode !== undefined) searchMode.value = c.search_mode;
                if (c.search_recency_filter !== undefined) searchRecencyFilter.value = c.search_recency_filter;
                if (c.search_language_filter) searchLanguageFilter.value = c.search_language_filter;
                if (c.search_domain_filter) searchDomainFilter.value = c.search_domain_filter;
                if (c.search_after_date_filter !== undefined) searchAfterDateFilter.value = c.search_after_date_filter;
                if (c.search_before_date_filter !== undefined) searchBeforeDateFilter.value = c.search_before_date_filter;
                if (c.last_updated_after_filter !== undefined) lastUpdatedAfterFilter.value = c.last_updated_after_filter;
                if (c.last_updated_before_filter !== undefined) lastUpdatedBeforeFilter.value = c.last_updated_before_filter;
                if (c.search_context_size !== undefined) searchContextSize.value = c.search_context_size;
                if (c.user_location_country !== undefined) userLocationCountry.value = c.user_location_country;
                if (c.user_location_latitude !== undefined) userLocationLatitude.value = c.user_location_latitude;
                if (c.user_location_longitude !== undefined) userLocationLongitude.value = c.user_location_longitude;
                if (c.user_location_city !== undefined) userLocationCity.value = c.user_location_city;
                if (c.user_location_region !== undefined) userLocationRegion.value = c.user_location_region;
                if (c.include_citations !== undefined) includeCitations.value = !!c.include_citations;
                // Ollama
                if (c.ollama_think !== undefined) ollamaThink.value = c.ollama_think;
                // Tools & MCP
                if (c.available_tools) availableTools.value = c.available_tools as ToolType[];
                if (c.mcp_servers) mcpServers.value = c.mcp_servers as ('playwright' | 'azdevops')[];
                if (c.knowledge_base_indexes) knowledgeBaseIndexes.value = c.knowledge_base_indexes;
                // Retries
                if (c.retries !== undefined) retries.value = c.retries;
                if (c.output_retries !== undefined) outputRetries.value = c.output_retries;
                // Agent behaviour
                if (c.end_strategy !== undefined) endStrategy.value = c.end_strategy;
                if (c.tool_timeout !== undefined) toolTimeout.value = c.tool_timeout;
                if (c.max_concurrency !== undefined) maxConcurrency.value = c.max_concurrency;
                // History
                if (c.history_length !== undefined) historyLength.value = c.history_length;
                // Output
                if (c.output_type) outputType.value = c.output_type as OutputType;
                if (c.output_schema !== undefined) outputSchema.value = c.output_schema;
                if (c.output_model_name) outputModelName.value = c.output_model_name;
                // Skills
                if (c.skills) {
                    // Strip auto-managed skill — subagent is injected
                    // at runtime based on sub_agent_ids.
                    const autoManaged = new Set(['subagent']);
                    skills.value = (c.skills as string[]).filter(s => !autoManaged.has(s)) as SkillType[];
                }
                // Summarization config
                if (c.summarization_max_messages !== undefined) summarizationMaxMessages.value = c.summarization_max_messages;
                if (c.summarization_max_tokens !== undefined) summarizationMaxTokens.value = c.summarization_max_tokens;
                if (c.summarization_keep_recent !== undefined) summarizationKeepRecent.value = c.summarization_keep_recent;
                if (c.workflow_ids) workflowIds.value = c.workflow_ids;
                if (c.guardrail_ids) guardrailIds.value = c.guardrail_ids;
                if (c.output_validation) outputValidation.value = c.output_validation;
                if (c.usage_limits) usageLimits.value = c.usage_limits;
                // Memory
                if (c.memory_enabled !== undefined) memoryEnabled.value = c.memory_enabled;
                if (c.memory_agent_name) memoryAgentName.value = c.memory_agent_name;
            }

            loadedAgentId.value = agent.id;
            loadedAgentName.value = agent.name;
            loadedAgentIsSystem.value = agent.is_system ?? false;
        };

        /**
         * Reset the chat config back to defaults (unload any agent).
         */
        const resetToDefaults = () => {
            loadedAgentId.value = null;
            loadedAgentName.value = '';
            loadedAgentIsSystem.value = false;
            prompt.value = DEFAULT_PROMPT;
            if (models.value.length > 0) currentModel.value = models.value[0].name;
            temperature.value = DEFAULT_TEMPERATURE;
            topP.value = DEFAULT_TOP_P;
            samplingMethod.value = 'temperature';
            timeout.value = DEFAULT_TIMEOUT;
            maxTokens.value = null;
            seed.value = null;
            presencePenalty.value = 0;
            frequencyPenalty.value = 0;
            parallelToolCalls.value = false;
            reasoningEffort.value = null;
            reasoningSummary.value = null;
            anthropicThinkingEnabled.value = false;
            anthropicCachingEnabled.value = false;
            searchMode.value = null;
            searchRecencyFilter.value = null;
            searchLanguageFilter.value = [];
            searchDomainFilter.value = [];
            searchAfterDateFilter.value = null;
            searchBeforeDateFilter.value = null;
            lastUpdatedAfterFilter.value = null;
            lastUpdatedBeforeFilter.value = null;
            searchContextSize.value = null;
            userLocationCountry.value = null;
            userLocationLatitude.value = null;
            userLocationLongitude.value = null;
            userLocationCity.value = null;
            userLocationRegion.value = null;
            ollamaThink.value = null;
            includeCitations.value = true;
            availableTools.value = [];
            mcpServers.value = [];
            knowledgeBaseIndexes.value = [];
            retries.value = DEFAULT_RETRIES;
            outputRetries.value = DEFAULT_OUTPUT_RETRIES;
            endStrategy.value = 'exhaustive';
            toolTimeout.value = null;
            maxConcurrency.value = null;
            historyLength.value = null;
            conversationHistory.value = [];
                sessionTranscript.value = [];
            agentSessions.value = [];
            activeSessionId.value = null;
            outputType.value = 'string';
            outputSchema.value = null;
            outputModelName.value = 'ResponseModel';
            skills.value = [];
            summarizationMaxMessages.value = 50;
            summarizationMaxTokens.value = null;
            summarizationKeepRecent.value = 10;
            subAgentIds.value = [];
            workflowIds.value = [];
            guardrailIds.value = [];
            outputValidation.value = [];
            usageLimits.value = {};
            memoryEnabled.value = false;
            memoryAgentName.value = 'agent';
            artifactRefs.value = [];
        };

        // Watch provider changes to auto-select valid AI provider
        watch(cloud_provider, (newProvider) => {
            const validAiProviders = LLM_PROVIDER_COMBINATIONS[newProvider]
            if (!validAiProviders.includes(aiProvider.value)) {
                aiProvider.value = validAiProviders[0]
            }
        })

        // Watch model changes to auto-set providers from model pricing
        watch(currentModel, (newModel) => {
            const modelInfo = models.value.find(m => m.name === newModel)
            if (modelInfo) {
                if (modelInfo.cloud_provider) {
                    cloud_provider.value = modelInfo.cloud_provider as ProviderType
                }
                if (modelInfo.model_provider) {
                    aiProvider.value = modelInfo.model_provider as ModelProviderType
                }
            }
        })

        // Computed JSON data for the chat request.
        // Agent configuration is loaded server-side from SubAgentDefinition.
        // The client only sends the agent_id, message, key, and history.
        const jsonData = computed(() => {
            if (!loadedAgentId.value) {
                return null
            }

            return {
                agent_id: loadedAgentId.value,
                message: message.value,
                key: messageKey.value || undefined,
                session_id: activeSessionId.value || undefined,
                // Include conversation history for multi-turn continuation
                history: (historyLength.value && historyLength.value > 0 && conversationHistory.value.length > 0)
                    ? conversationHistory.value.slice(-(historyLength.value * 2))
                    : undefined,
                artifact_refs: artifactRefs.value.length > 0 ? artifactRefs.value : undefined,
                target_hint: targetHint.value || undefined,
            }
        })

        const {handleAxiosError, rootAuth} = useWrapper();
        const {guid, axiosBearerHeader, portalBaseUrl} = useApiCredentials();
        const subAgentRoute = computed(() => `${portalBaseUrl}/${guid.value}/agent-definition`)
        const subAgentTeamRoute = computed(() => `${subAgentRoute.value}/team`)

        const buildSubAgentDiscoveryParams = (filters?: SubAgentDiscoveryFilters): URLSearchParams => {
            const params = new URLSearchParams()
            if (!filters) return params
            if (filters.team?.trim()) params.set('team', filters.team.trim())
            if (filters.sub_team?.trim()) params.set('sub_team', filters.sub_team.trim())
            if (filters.visibility?.trim()) params.set('visibility', filters.visibility.trim())
            return params
        }

        const loadAgentSessions = handleAxiosError(
            rootAuth(async (agentId?: number): Promise<AgentSession[]> => {
                sessionLoading.value = true
                try {
                    debugLog('loadAgentSessions:start', {
                        agentId,
                        activeSessionId: activeSessionId.value,
                    });
                    const { data } = await axios.get<AgentSession[]>(
                        `/${guid.value}/agent/sessions`,
                        axiosBearerHeader.value,
                    )
                    agentSessions.value = typeof agentId === 'number'
                        ? data.filter((session) => session.agent_id === agentId)
                        : data

                    if (activeSessionId.value && !agentSessions.value.some((session) => session.id === activeSessionId.value)) {
                        debugLog('loadAgentSessions:activeSessionMissing_reset', {
                            previousActiveSessionId: activeSessionId.value,
                        });
                        activeSessionId.value = null
                    }
                    debugLog('loadAgentSessions:success', {
                        totalSessions: data.length,
                        filteredSessions: agentSessions.value.length,
                        firstSessionId: agentSessions.value[0]?.id ?? null,
                        activeSessionId: activeSessionId.value,
                    });
                    return agentSessions.value
                } finally {
                    sessionLoading.value = false
                }
            }, false),
            { errorMsg: 'stores.agent.api_error' },
        )

        const createAgentSession = handleAxiosError(
            rootAuth(async (agentId: number, title?: string): Promise<AgentSession | undefined> => {
                sessionLoading.value = true
                try {
                    debugLog('createAgentSession:start', { agentId, title: title || 'New Session' });
                    const { data } = await axios.post<AgentSession>(
                        `/${guid.value}/agent/sessions`,
                        {
                            agent_id: agentId,
                            title: title || 'New Session',
                        },
                        axiosBearerHeader.value,
                    )
                    agentSessions.value = [data, ...agentSessions.value.filter((session) => session.id !== data.id)]
                    activeSessionId.value = data.id
                    debugLog('createAgentSession:success', {
                        createdSessionId: data.id,
                        activeSessionId: activeSessionId.value,
                        sessionCount: agentSessions.value.length,
                    });
                    return data
                } finally {
                    sessionLoading.value = false
                }
            }, false),
            { errorMsg: 'stores.agent.api_error' },
        )

        const deleteAgentSession = handleAxiosError(
            rootAuth(async (sessionId: number): Promise<void> => {
                sessionLoading.value = true
                try {
                    await axios.delete(
                        `/${guid.value}/agent/sessions/${sessionId}`,
                        axiosBearerHeader.value,
                    )
                    agentSessions.value = agentSessions.value.filter((session) => session.id !== sessionId)
                    if (activeSessionId.value === sessionId) {
                        activeSessionId.value = null
                    }
                } finally {
                    sessionLoading.value = false
                }
            }, false),
            { errorMsg: 'stores.agent.api_error' },
        )

        const loadAgentSessionHistory = handleAxiosError(
            rootAuth(async (sessionId: number): Promise<any[]> => {
                sessionLoading.value = true
                try {
                    debugLog('loadAgentSessionHistory:start', {
                        sessionId,
                        activeSessionId: activeSessionId.value,
                    });
                        const { data } = await axios.get<{
                            session_id: number;
                            history: any[];
                            transcript?: Array<{ role: 'user' | 'assistant'; content: string }>;
                        }>(
                        `/${guid.value}/agent/sessions/${sessionId}/history`,
                        axiosBearerHeader.value,
                    )
                    const history = Array.isArray(data.history) ? data.history : []
                    setHistoryFromResponse(history)
                        sessionTranscript.value = Array.isArray(data.transcript) ? data.transcript : [];
                    debugLog('loadAgentSessionHistory:success', {
                        sessionId: data.session_id,
                        historyLength: history.length,
                        transcriptLength: sessionTranscript.value.length,
                        historyKinds: history
                            .map((entry) => (entry && typeof entry === 'object' ? (entry as Record<string, unknown>).kind : null))
                            .filter((kind) => typeof kind === 'string')
                            .slice(0, 6),
                    });
                    return history
                } finally {
                    sessionLoading.value = false
                }
            }, false),
            { errorMsg: 'stores.agent.api_error' },
        )

        const getAgentSessionExecution = handleAxiosError(
            rootAuth(async (sessionId: number): Promise<{
                session_id: number;
                execution_id: number | null;
                run_id: string | null;
                status: string | null;
                is_active: boolean;
            }> => {
                const { data } = await axios.get<{
                    session_id: number;
                    execution_id: number | null;
                    run_id: string | null;
                    status: string | null;
                    is_active: boolean;
                }>(
                    `/${guid.value}/agent/sessions/${sessionId}/execution`,
                    axiosBearerHeader.value,
                )
                debugLog('getAgentSessionExecution:success', {
                    sessionId: data.session_id,
                    executionId: data.execution_id,
                    runId: data.run_id,
                    status: data.status,
                    isActive: data.is_active,
                })
                return data
            }, false),
            { errorMsg: 'stores.agent.api_error' },
        )

        const resumeAgentExecution = async (runId: string): Promise<void> => {
            debugLog('resumeAgentExecution:start', {
                runId,
                activeSessionId: activeSessionId.value,
            })
            const baseUrl = axios.defaults.baseURL || ''
            const url = `${baseUrl}/${guid.value}/agent/${runId}/stream`
            await sse.resumeStream(url)
            debugLog('resumeAgentExecution:completed', {
                runId,
                error: sse.error.value || null,
                sseHistoryLength: Array.isArray(sse.history.value) ? sse.history.value.length : 0,
                timelineItems: sse.conversationTimeline.value.length,
                streamedTextLength: sse.streamedText.value?.length || 0,
            })
        }

        // ── Chat Actions ─────────────────────────────────────────────────

        const get_response = handleAxiosError<IAgentStore["get_response"]>(
            rootAuth(
                async (): Promise<[]> => {
                    const {data} = await axios.post<[]>(
                        `/${guid.value}/agent`,
                        jsonData.value,
                        axiosBearerHeader.value,
                    );
                    return data;
                }, false),
            {errorMsg: "stores.agent.api_error"}
        );

        /**
         * Start a streaming response via Server-Sent Events.
         * Calls ``POST /{guid}/agent/stream`` and delegates to the
         * ``useSSE`` composable which updates reactive refs in real-time.
         */
        const stream_response = async (): Promise<void> => {
            if (!jsonData.value) {
                sse.error.value = 'No agent loaded — please select or create an agent first.'
                return
            }
            debugLog('stream_response:start', {
                agentId: jsonData.value.agent_id,
                sessionId: jsonData.value.session_id ?? null,
                historyLengthInPayload: Array.isArray(jsonData.value.history) ? jsonData.value.history.length : 0,
                artifactRefCount: Array.isArray(jsonData.value.artifact_refs) ? jsonData.value.artifact_refs.length : 0,
            });
            const baseUrl = axios.defaults.baseURL || ''
            const url = `${baseUrl}/${guid.value}/agent/stream`
            await sse.startStream(url, jsonData.value)
            debugLog('stream_response:completed', {
                error: sse.error.value || null,
                sseHistoryLength: Array.isArray(sse.history.value) ? sse.history.value.length : 0,
                timelineItems: sse.conversationTimeline.value.length,
                streamedTextLength: sse.streamedText.value?.length || 0,
            });
        }

        const get_available_models = handleAxiosError(
            rootAuth(
                async (): Promise<IAvailableModels[]> => {
                    const {data} = await axios.get<IAvailableModels[]>(
                        `${portalBaseUrl}/${guid.value}/available/models`,
                        axiosBearerHeader.value,
                    );
                    const llmModels = data.filter(m => !m.config_type || m.config_type === 'llm');
                    const previousModel = currentModel.value;
                    models.value = llmModels;

                    // Preserve current selection if it still exists in the refreshed list
                    const previousStillExists = previousModel && llmModels.some(m => m.name === previousModel);
                    if (previousStillExists) {
                        // Model still available — keep it (no-op on currentModel)
                    } else if (llmModels.length > 0) {
                        currentModel.value = llmModels[0].name
                        if (llmModels[0].cloud_provider) {
                            cloud_provider.value = llmModels[0].cloud_provider as ProviderType
                        }
                        if (llmModels[0].model_provider) {
                            aiProvider.value = llmModels[0].model_provider as ModelProviderType
                        }
                    } else {
                        currentModel.value = "No available Model"
                    }
                    return data;
                }
                , false),
            {errorMsg: "stores.agent.api_error"})

        // Helper to get valid AI providers for current provider
        const getValidAiProviders = computed(() => LLM_PROVIDER_COMBINATIONS[cloud_provider.value])

        // Set history from AgentResponseModel.history (pydantic-ai format)
        const setHistoryFromResponse = (history: any[]) => {
            if (history && Array.isArray(history)) {
                // The backend returns the authoritative current history.
                // Replacing avoids quadratic duplication across turns.
                conversationHistory.value = [...history]
                debugLog('setHistoryFromResponse', {
                    historyLength: conversationHistory.value.length,
                    firstKind:
                        conversationHistory.value.length > 0
                            && conversationHistory.value[0]
                            && typeof conversationHistory.value[0] === 'object'
                            ? (conversationHistory.value[0] as Record<string, unknown>).kind
                            : null,
                });
            }
        }

        // Clear conversation history
        const clearHistory = () => {
            conversationHistory.value = []
            sessionTranscript.value = []
            debugLog('clearHistory');
        }

        // Get the number of conversation turns (pairs of request/response)
        const historyTurns = computed(() => Math.floor(conversationHistory.value.length / 2))

        // Reset memory for the current agent
        const reset_memory = handleAxiosError(
            rootAuth(
                async (): Promise<{ success: boolean; message: string }> => {
                    const processId = `agent_${memoryAgentName.value}`;
                    const { data } = await axios.delete<{ success: boolean; message: string }>(
                        `/${guid.value}/memory/${processId}`,
                        axiosBearerHeader.value,
                    );
                    return data;
                }, false),
            { errorMsg: "stores.agent.reset_memory_error" }
        );

        // Reset all memory for the app
        const reset_all_memory = handleAxiosError(
            rootAuth(
                async (): Promise<{ success: boolean; message: string }> => {
                    const { data } = await axios.delete<{ success: boolean; message: string }>(
                        `/${guid.value}/memory`,
                        axiosBearerHeader.value,
                    );
                    return data;
                }, false),
            { errorMsg: "stores.agent.reset_all_memory_error" }
        );

        // ── Agent Definition CRUD ───────────────────────────────────────────────

        const loadSubAgents = handleAxiosError(
            rootAuth(async (): Promise<void> => {
                subAgentLoading.value = true
                try {
                    const { data } = await axios.get<SubAgent[]>(
                        subAgentRoute.value,
                        axiosBearerHeader.value,
                    )
                    subAgents.value = data
                } finally {
                    subAgentLoading.value = false
                }
            }, false),
            { errorMsg: 'stores.subAgent.load_error' },
        )

        const createSubAgent = handleAxiosError(
            rootAuth(async (body: CreateSubAgentRequest): Promise<SubAgent | undefined> => {
                subAgentLoading.value = true
                try {
                    const { data } = await axios.post<SubAgent>(
                        subAgentRoute.value,
                        body,
                        axiosBearerHeader.value,
                    )
                    subAgents.value.push(data)
                    return data
                } finally {
                    subAgentLoading.value = false
                }
            }, false),
            {
                errorMsg: 'stores.subAgent.create_error',
                successMsg: 'stores.subAgent.create_success',
            },
        )

        const updateSubAgent = handleAxiosError(
            rootAuth(async (id: number, body: UpdateSubAgentRequest): Promise<SubAgent | undefined> => {
                subAgentLoading.value = true
                try {
                    const { data } = await axios.put<SubAgent>(
                        `${subAgentRoute.value}/${id}`,
                        body,
                        axiosBearerHeader.value,
                    )
                    const idx = subAgents.value.findIndex((a) => a.id === id)
                    if (idx !== -1) subAgents.value[idx] = data
                    if (currentSubAgent.value?.id === id) currentSubAgent.value = data
                    return data
                } finally {
                    subAgentLoading.value = false
                }
            }, false),
            {
                errorMsg: 'stores.subAgent.update_error',
                successMsg: 'stores.subAgent.update_success',
            },
        )

        const deleteSubAgent = handleAxiosError(
            rootAuth(async (id: number): Promise<void> => {
                subAgentLoading.value = true
                try {
                    await axios.delete(
                        `${subAgentRoute.value}/${id}`,
                        axiosBearerHeader.value,
                    )
                    subAgents.value = subAgents.value.filter((a) => a.id !== id)
                    if (currentSubAgent.value?.id === id) currentSubAgent.value = null
                } finally {
                    subAgentLoading.value = false
                }
            }, false),
            {
                errorMsg: 'stores.subAgent.delete_error',
                successMsg: 'stores.subAgent.delete_success',
            },
        )

        const getSubAgent = handleAxiosError(
            rootAuth(async (id: number): Promise<SubAgent | undefined> => {
                const { data } = await axios.get<SubAgent>(
                    `${subAgentRoute.value}/${id}`,
                    axiosBearerHeader.value,
                )
                currentSubAgent.value = data
                return data
            }, false),
            { errorMsg: 'stores.subAgent.load_error' },
        )

        const loadSubAgentTeams = handleAxiosError(
            rootAuth(async (): Promise<void> => {
                const { data } = await axios.get<SubAgentTeam[]>(
                    subAgentTeamRoute.value,
                    axiosBearerHeader.value,
                )
                subAgentTeams.value = data
            }, false),
            { errorMsg: 'stores.subAgent.load_error' },
        )

        const createSubAgentTeam = handleAxiosError(
            rootAuth(async (body: CreateSubAgentTeamRequest): Promise<SubAgentTeam | undefined> => {
                const { data } = await axios.post<SubAgentTeam>(
                    subAgentTeamRoute.value,
                    body,
                    axiosBearerHeader.value,
                )
                subAgentTeams.value.push(data)
                return data
            }, false),
            {
                errorMsg: 'stores.subAgent.create_error',
                successMsg: 'stores.subAgent.create_success',
            },
        )

        const updateSubAgentTeam = handleAxiosError(
            rootAuth(async (id: number, body: UpdateSubAgentTeamRequest): Promise<SubAgentTeam | undefined> => {
                const { data } = await axios.put<SubAgentTeam>(
                    `${subAgentTeamRoute.value}/${id}`,
                    body,
                    axiosBearerHeader.value,
                )
                const idx = subAgentTeams.value.findIndex((team) => team.id === id)
                if (idx !== -1) subAgentTeams.value[idx] = data
                return data
            }, false),
            {
                errorMsg: 'stores.subAgent.update_error',
                successMsg: 'stores.subAgent.update_success',
            },
        )

        const deleteSubAgentTeam = handleAxiosError(
            rootAuth(async (id: number): Promise<void> => {
                await axios.delete(
                    `${subAgentTeamRoute.value}/${id}`,
                    axiosBearerHeader.value,
                )
                subAgentTeams.value = subAgentTeams.value.filter((team) => team.id !== id)
            }, false),
            {
                errorMsg: 'stores.subAgent.delete_error',
                successMsg: 'stores.subAgent.delete_success',
            },
        )

        const loadPhonebook = handleAxiosError(
            rootAuth(async (filters?: SubAgentDiscoveryFilters): Promise<void> => {
                const query = buildSubAgentDiscoveryParams(filters).toString()
                const url = query
                    ? `${subAgentRoute.value}/phonebook?${query}`
                    : `${subAgentRoute.value}/phonebook`
                const { data } = await axios.get<SubAgentPhonebookEntry[]>(
                    url,
                    axiosBearerHeader.value,
                )
                phonebook.value = data
            }, false),
            { errorMsg: 'stores.subAgent.load_error' },
        )

        const searchByTags = handleAxiosError(
            rootAuth(async (tags: string[], filters?: SubAgentDiscoveryFilters): Promise<SubAgentPhonebookEntry[]> => {
                const params = buildSubAgentDiscoveryParams(filters)
                for (const tag of tags) {
                    if (tag?.trim()) params.append('tags', tag.trim())
                }
                const { data } = await axios.get<SubAgentPhonebookEntry[]>(
                    `${subAgentRoute.value}/search?${params.toString()}`,
                    axiosBearerHeader.value,
                )
                return data
            }, false),
            { errorMsg: 'stores.subAgent.load_error' },
        )

        // ── Discovery ────────────────────────────────────────────────────

        const discoverCoworkers = handleAxiosError(
            rootAuth(async (discoverMessage: string, model?: string): Promise<IDiscoverMatch[]> => {
                const { data } = await axios.post<{ matches: IDiscoverMatch[] }>(
                    `${portalBaseUrl}/${guid.value}/agent/discover`,
                    { message: discoverMessage, ...(model ? { model } : {}) },
                    axiosBearerHeader.value,
                )
                return data.matches
            }, false),
            { errorMsg: 'stores.agent.discover_error' },
        )

        // ── Agent Approval ───────────────────────────────────────────────

        const getAgentApproval = handleAxiosError(
            rootAuth(async (agentId: number): Promise<Record<string, unknown> | null> => {
                const { data } = await axios.get<Record<string, unknown> | null>(
                    `${subAgentRoute.value}/${agentId}/approval`,
                    axiosBearerHeader.value,
                )
                return data
            }, false),
            { errorMsg: 'stores.subAgent.approval_load_error' },
        )

        const requestAgentApproval = handleAxiosError(
            rootAuth(async (agentId: number, body: Record<string, unknown>): Promise<Record<string, unknown> | undefined> => {
                const { data } = await axios.post<Record<string, unknown>>(
                    `${subAgentRoute.value}/${agentId}/approval/request`,
                    body,
                    axiosBearerHeader.value,
                )
                return data
            }, false),
            {
                errorMsg: 'stores.subAgent.approval_submit_error',
                successMsg: 'stores.subAgent.approval_submit_success',
            },
        )

        initialized.value = true;
        return {
            initialized,
            models,
            items,
            // Provider config
            cloud_provider,
            aiProvider,
            getValidAiProviders,
            // Model params
            currentModel,
            temperature,
            maxTokens,
            topP,
            timeout,
            seed,
            samplingMethod,
            // Penalties
            presencePenalty,
            frequencyPenalty,
            // Reasoning
            reasoningEffort,
            reasoningSummary,
            // Anthropic Thinking
            anthropicThinkingEnabled,
            anthropicThinkingEffort,
            anthropicThinkingBudgetTokens,
            anthropicInterleavedThoughts,
            anthropicAdaptiveThoughts,
            // Anthropic Caching
            anthropicCachingEnabled,
            anthropicCacheInstructions,
            anthropicCacheToolDefinitions,
            anthropicCacheMessages,
            // Perplexity
            searchMode,
            searchRecencyFilter,
            searchLanguageFilter,
            searchDomainFilter,
            searchAfterDateFilter,
            searchBeforeDateFilter,
            lastUpdatedAfterFilter,
            lastUpdatedBeforeFilter,
            searchContextSize,
            userLocationCountry,
            userLocationLatitude,
            userLocationLongitude,
            userLocationCity,
            userLocationRegion,
            includeCitations,
            // Ollama
            ollamaThink,
            // Tools
            availableTools,
            mcpServers,
            knowledgeBaseIndexes,
            // Retries
            retries,
            outputRetries,
            // Agent behaviour
            endStrategy,
            toolTimeout,
            maxConcurrency,
            // Tool execution
            parallelToolCalls,
            // History (pydantic-ai format)
            historyLength,
            conversationHistory,
                sessionTranscript,
            agentSessions,
            activeSessionId,
            sessionLoading,
            setHistoryFromResponse,
            clearHistory,
            historyTurns,
            // Output config
            outputType,
            outputSchema,
            outputModelName,
            // Memory configuration
            memoryEnabled,
            memoryAgentName,
            reset_memory,
            reset_all_memory,
            // Message
            messageKey,
            prompt,
            message,
            artifactRefs,
            targetHint,
            jsonData,
            // Streaming
            sse,
            // Skills
            skills,
            summarizationMaxMessages,
            summarizationMaxTokens,
            summarizationKeepRecent,
            subAgentIds,
            workflowIds,
            guardrailIds,
            outputValidation,
            usageLimits,
            // Agent Definition CRUD
            subAgents,
            subAgentTeams,
            phonebook,
            currentSubAgent,
            subAgentLoading,
            loadSubAgents,
            createSubAgent,
            updateSubAgent,
            deleteSubAgent,
            getSubAgent,
            loadSubAgentTeams,
            createSubAgentTeam,
            updateSubAgentTeam,
            deleteSubAgentTeam,
            loadPhonebook,
            searchByTags,
            loadAgentSessions,
            createAgentSession,
            deleteAgentSession,
            loadAgentSessionHistory,
            getAgentSessionExecution,
            resumeAgentExecution,
            // Discovery
            discoverCoworkers,
            // Loaded agent tracking
            loadedAgentId,
            loadedAgentName,
            loadedAgentIsSystem,
            isNonDefaultConfig,
            buildFullConfig,
            loadAgentConfig,
            resetToDefaults,
            // Agent Approval
            getAgentApproval,
            requestAgentApproval,
            // Actions
            get_response,
            stream_response,
            get_available_models
        };
    }
);


